<template>
  <main id="content" aria-labelledby="main-title" v-if="page">
    
    <div class="hero--info bg-faun">
      <div class="pt-16 sm:pt-24">
        <Container>
          <FadeIn class="text-center max-w-3xl mx-auto">
            <div class="mb-2 md:mb-4">
              <NuxtLink :to="$localePath('/blog')" class="rounded-full mr-2 sm:mr-0 mb-2 sm:mb-0 bg-[#5a6ded] border-2 border-[#3645d0] px-2 py-0 text-xs inline-block font-semibold leading-6 text-white no-underline">
                Blog
              </NuxtLink>
            </div>
            <h1 id="main-title" class="text-section-title">
              {{ page.heading }}
            </h1>
            <p class="text-subtitle">
              {{ page.subtitle }}
            </p>
          </FadeIn>
        </Container>
      </div>
    </div>

    <ElementBlogCategories v-if="categories" :categories="categories" />

    <Section margins="reset-top">
      <Container>

        <FadeIn 
        delay="200"
        class="mx-auto mt-0 grid grid-cols-1 gap-x-8 gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-3"
        >
          <ElementBlogCard v-for="(article, index) in articles" :key="'article-'+index" :article="article" />
        </FadeIn>

      </Container>
    </Section>

    <ElementBookDemoCta />

  </main>
</template>

<script setup>
// Publish gate: an unbuilt CMS page 404s until the next deploy prerenders it.
useRequirePrerendered()

const route = useRoute()
const { getItems } = useCms()
const postStatus = useRuntimeConfig().public.postStatus

const currentLocale = computed(() => {
  return route.path.startsWith('/nz') ? 'nz' : 'au'
})

const { data, error } = await useAsyncData(
  // Reactive key: refetches on slug/locale change without a full page remount
  // (see specs/remove-global-page-key.md).
  () => `${currentLocale.value}-category-page-${route.params.slug}`,
  async () => {
    
    const pageData = await getItems({
      collection: 'category',
      params: {
        filter: {
          slug: {
            "_eq": route.params.slug
          }
        },
        fields: [
          '*',
          'seo.*',
          'seo.meta_title', 
          'seo.meta_description',
          'seo.meta_image.*'
        ]
      }
    })
    
    // Missing category → return an empty result and let the setup-scope 404 below
    // handle it. Throwing here does NOT surface a 404 (Nuxt captures a handler throw
    // into asyncData.error); it also skips the remaining fetches.
    if (!pageData?.length) {
      return { page: null, articles: [], categories: [] }
    }

    const articlesData = await getItems({
      collection: 'article',
      params: {
        filter: {
          locale: { _contains: currentLocale.value },
          status: {
            _in: postStatus
          },
          categories: {
            category_id: {
              slug: {
                _eq: route.params.slug
              }
            }
          }
        },
        fields: [
          '*', 'image.id', 'image.filename_download', 'image.description', 'image.width', 'image.height', 'category.category_id.title', 'category.category_id.slug',
        ],
        sort: "-date"
      }
    })
    
    const categoriesData = await getItems({
      collection: 'category',
      params: {
        filter: {
          locale: {
            _contains: currentLocale.value
          }
        },
        fields: [
          '*'
        ]
      }
    })
    
    // Return all data in a single object
    return {
      page: pageData[0],
      articles: articlesData,
      categories: categoriesData
    }
  }
)

// 404 ONLY for a genuinely missing category — the fetch SUCCEEDED and returned no page
// (the handler returns { page: null } for that case). A fetch error (CMS 5xx / timeout)
// sets `error`; do not 404 a real category over a transient blip (Nuxt surfaces the
// fetch error). Thrown in setup for the initial/SSR render, re-checked via a watch for
// client navigation (setup does not re-run without a remount).
const notFound = () => createError({ statusCode: 404, message: 'Category not found', fatal: true })
if (!error.value && !data.value?.page) {
  throw notFound()
}
watch([data, error], ([val, err]) => {
  if (!err && !val?.page) showError(notFound())
})

const page = computed(() => data.value?.page)
const articles = computed(() => data.value?.articles)
const categories = computed(() => data.value?.categories)

// SEO handling
const seo = computed(() => {
  if (page.value?.seo) {
    return useSeo(page.value.seo, route.fullPath)
  }
  return {}
})

useHead(seo)

</script>